/**
 * PlanStatusesAdmin.tsx — Admin > Plans > Plan Statuses.
 *
 * FTP's page (lib/admin/admin-plan-status-list.php) is the spec: each live
 * stage of the account, in pipeline order, with the statuses a plan in that
 * stage can carry, on one board (CategoryBoard, ordered). The stage order is
 * the pipeline itself and a stage's status order is what its pickers show, so
 * neither is sorted A–Z (KTD13):
 *
 *   - stages are listed as the API gives them (Inquiry first, then the saved
 *     pipeline order); the Stage Pipeline page orders them;
 *   - a status drags to reorder it inside its stage, or onto another stage to
 *     move it there, where the drop spot is its place. A drop saves the target
 *     stage's whole list — its deleted statuses too, after the live ones, so
 *     every orderID in the stage is rewritten 1..n with no collisions;
 *   - Add (per stage) and rename (a click on the status) open a small form;
 *   - delete is soft, asks first, and Show deleted lists the status, amber,
 *     with a Deleted badge and Restore (R18, R26). FTP tinted it the same way.
 *
 * Plans are never touched: a plan keeps a status that moves or is deleted
 * (R30). After any write the page refreshes itself, the plan status pickers
 * (plan-stage-subs) and the CRM plans by prefix (KTD14). Its query key sits
 * under the Stage Pipeline page's root, so a stage rename, reorder or delete
 * there refreshes this board too.
 *
 * On a Locked page (useAdminPageLevel) the board only reads: no add, edit,
 * delete, restore or drag (the shared blocks), no column header (its Actions
 * column would be empty), and no wording that invites a drag or an add. A
 * status still opens, read-only.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminBoardPage, AdminListEmpty } from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { CategoryBoard, boardDragHint, type BoardCategory } from '@/components/admin/CategoryBoard';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { STAGE_PIPELINE_QUERY_ROOT } from '@/components/admin/stagePipeline/stagePipelineModel';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Switch } from '@/components/ui/Switch';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  PLAN_STATUS_NAME_MAX,
  createPlanStatus,
  deletePlanStatus,
  listPlanStatusStages,
  renamePlanStatus,
  reorderPlanStatuses,
  restorePlanStatus,
  type PlanStatusEntry,
  type PlanStatusStage,
} from '@/services/planStatusesAdmin.service';
import { toast } from '@/store/toastStore';

/** This page's query: under the Stage Pipeline root, so that page's writes refresh it. */
export const PLAN_STATUSES_QUERY_KEY = [STAGE_PIPELINE_QUERY_ROOT, 'plan-statuses'] as const;

/** The screens that read statuses: every plan's status picker, and the CRM plans (KTD14). */
const STATUS_CACHE_PREFIXES = ['plan-stage-subs', 'crm-plans'] as const;

const stageName = (s: { name: string; id: number }) => s.name.trim() || `Stage #${s.id}`;
const statusName = (s: { name: string; id: number }) => s.name.trim() || `Status #${s.id}`;

const STATUS_COLUMNS = (
  <div className="flex items-center pr-2">
    <span className="min-w-0 flex-1">Status</span>
  </div>
);

/** The stages with `ids` filed under `stageId` in that order (orders 1..n), as the API saves a drop. */
function withStageOrder(stages: PlanStatusStage[], stageId: number, ids: number[]): PlanStatusStage[] {
  const byId = new Map(stages.flatMap((s) => s.statuses).map((x) => [x.id, x]));
  return stages.map((s) => {
    if (s.id === stageId) {
      const filed = ids.flatMap((id, i) => {
        const x = byId.get(id);
        return x ? [{ ...x, stageId, order: i + 1 }] : [];
      });
      return { ...s, statuses: filed };
    }
    return { ...s, statuses: s.statuses.filter((x) => !ids.includes(x.id)) };
  });
}

export function PlanStatusesAdmin() {
  const queryClient = useQueryClient();
  const locked = useAdminPageLevel() === 'locked';
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<{ status: PlanStatusEntry | null; stage: PlanStatusStage } | null>(null);
  const [deleting, setDeleting] = useState<PlanStatusEntry | null>(null);

  const stagesQuery = useQuery({ queryKey: PLAN_STATUSES_QUERY_KEY, queryFn: listPlanStatusStages });
  const stages = useMemo(() => stagesQuery.data ?? [], [stagesQuery.data]);

  /** This page and, by prefix, every screen that reads statuses (KTD14). */
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: PLAN_STATUSES_QUERY_KEY });
    for (const key of STATUS_CACHE_PREFIXES) void queryClient.invalidateQueries({ queryKey: [key] });
  }, [queryClient]);

  /** Shows a saved status at once; the refetch that follows confirms it. */
  const patchStatus = useCallback(
    (id: number, patch: Partial<PlanStatusEntry>) => {
      queryClient.setQueryData<PlanStatusStage[]>(PLAN_STATUSES_QUERY_KEY, (old) =>
        old?.map((s) => ({ ...s, statuses: s.statuses.map((x) => (x.id === id ? { ...x, ...patch } : x)) })),
      );
    },
    [queryClient],
  );

  // Stages in the API's (pipeline) order; each stage's statuses in their saved order.
  const board = useMemo(() => {
    const categories: BoardCategory[] = stages.map((s) => ({ id: s.id, title: stageName(s) }));
    const records = stages.flatMap((s) => s.statuses.filter((x) => showDeleted || x.active).map((x) => ({ ...x, stageId: s.id })));
    return { categories, records };
  }, [stages, showDeleted]);

  const stageOf = (status: PlanStatusEntry) => stages.find((s) => s.id === status.stageId);

  /**
   * Saves a drop: the target stage's live statuses in their new order, then
   * its deleted ones in their saved order, so the API rewrites every orderID
   * in the stage. A status dragged in from another stage is filed here; the
   * stage it left keeps its order. Throws on failure, so the board rolls back.
   */
  async function saveDrop(stageId: number, ids: number[]) {
    const stage = stages.find((s) => s.id === stageId);
    const deleted = (stage?.statuses ?? []).filter((x) => !x.active && !ids.includes(x.id)).map((x) => x.id);
    const full = [...ids, ...deleted];
    await reorderPlanStatuses(stageId, full);
    queryClient.setQueryData<PlanStatusStage[]>(PLAN_STATUSES_QUERY_KEY, (old) => (old ? withStageOrder(old, stageId, full) : old));
    refresh();
  }

  async function handleDelete() {
    if (!deleting) return;
    const target = deleting;
    try {
      await deletePlanStatus(target.id);
      patchStatus(target.id, { active: false });
      toast.success(`${statusName(target)} deleted`);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The status could not be deleted.'));
    }
  }

  async function handleRestore(status: PlanStatusEntry) {
    try {
      await restorePlanStatus(status.id);
      patchStatus(status.id, { active: true });
      toast.success(`${statusName(status)} restored`);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The status could not be restored.'));
    }
  }

  function openRename(status: PlanStatusEntry) {
    const stage = stageOf(status);
    if (status.active && stage) setEditing({ status, stage });
  }

  function statusRow(status: PlanStatusEntry) {
    const deleted = !status.active;
    return (
      <button
        type="button"
        className={cn(
          'flex w-full items-center py-2 pr-2 text-left',
          deleted && 'my-1 rounded-md bg-amber-50 px-2 py-1 text-amber-800 line-through',
        )}
        onClick={() => openRename(status)}
        disabled={deleted}
      >
        <span className="min-w-0 flex-1 truncate" data-testid="plan-status-name">
          {statusName(status)}
        </span>
      </button>
    );
  }

  let content: ReactNode;
  if (stagesQuery.isLoading) {
    content = <SpinnerOverlay label="Loading plan statuses…" />;
  } else if (stagesQuery.isError) {
    content = (
      <AdminListEmpty title="The plan statuses could not be loaded" hint={getErrorMessage(stagesQuery.error, 'Try again in a moment.')} />
    );
  } else if (board.categories.length === 0) {
    content = (
      <AdminListEmpty title="No stages yet" hint={locked ? undefined : 'Add stages on the Stage Pipeline page, then add their statuses here.'} />
    );
  } else {
    content = (
      <CategoryBoard<PlanStatusEntry>
        categories={board.categories}
        records={board.records}
        getRecordId={(x) => x.id}
        getRecordCategoryId={(x) => x.stageId}
        renderRecord={statusRow}
        columnHeader={locked ? undefined : STATUS_COLUMNS}
        renderCategoryActions={(category) => {
          const stage = stages.find((s) => s.id === category.id);
          if (!stage) return null;
          return (
            <AdminRowAction
              kind="add"
              title="Add status"
              label={`Add status to ${stageName(stage)}`}
              onClick={() => setEditing({ status: null, stage })}
            />
          );
        }}
        renderRecordActions={(status) => {
          const name = statusName(status);
          if (!status.active) {
            return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(status)} />;
          }
          return (
            <>
              <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => openRename(status)} />
              <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(status)} />
            </>
          );
        }}
        isRecordDeleted={(x) => !x.active}
        ordered
        onReorderRecords={saveDrop}
        onSaveError={(err) => toast.error(getErrorMessage(err, 'The new order could not be saved.'))}
        recordNoun={['status', 'statuses']}
        dragDisabled={showDeleted}
      />
    );
  }

  return (
    <AdminBoardPage
      title="Plan Statuses"
      subtitle={
        locked
          ? 'The statuses a plan can carry in each stage, in pipeline order. Plans keep their status when it is moved or deleted.'
          : 'The statuses a plan can carry in each stage, in pipeline order. Drag a status to reorder it, or onto another stage to move it there. Plans keep their status when it is moved or deleted.'
      }
      toolbar={
        <div className="flex items-center justify-end gap-2">
          <label htmlFor="plan-statuses-show-deleted" className="cursor-pointer select-none text-xs text-slate-500">
            Show deleted
          </label>
          <Switch id="plan-statuses-show-deleted" checked={showDeleted} onChange={setShowDeleted} />
        </div>
      }
      hint={boardDragHint({ searching: false, filtering: false, showDeleted })}
      dialogs={
        <>
          {editing && (
            <PlanStatusFormModal
              key={editing.status?.id ?? `new-${editing.stage.id}`}
              status={editing.status}
              stage={editing.stage}
              onClose={() => setEditing(null)}
              onSaved={(saved) => {
                if (editing.status) patchStatus(editing.status.id, { name: saved.name });
                setEditing(null);
                refresh();
              }}
            />
          )}
          <ConfirmDialog
            isOpen={deleting !== null}
            onClose={() => setDeleting(null)}
            onConfirm={handleDelete}
            title={deleting ? `Delete status "${statusName(deleting)}"?` : ''}
            message="It leaves the status pickers. Plans that have it keep it. You can restore it from Show deleted."
            confirmLabel="Delete status"
            variant="danger"
          />
        </>
      }
    >
      {content}
    </AdminBoardPage>
  );
}

// ─── Add / rename form ────────────────────────────────────────────────────────

function PlanStatusFormModal({
  status,
  stage,
  onClose,
  onSaved,
}: {
  /** null: a new status, added last in `stage`. */
  status: PlanStatusEntry | null;
  stage: PlanStatusStage;
  onClose: () => void;
  onSaved: (saved: { name: string }) => void;
}) {
  const isNew = status === null;
  const t = useTrackedForm(() => ({ name: status?.name ?? '' }), { tracking: !isNew });
  const [saving, setSaving] = useState(false);
  const name = t.form.name.trim();
  const missing = name === '';
  const tooLong = name.length > PLAN_STATUS_NAME_MAX;
  const errors = [
    ...(missing ? ['Name is required'] : []),
    ...(tooLong ? [`Name must be ${PLAN_STATUS_NAME_MAX} characters or fewer`] : []),
  ];

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      if (status) await renamePlanStatus(status.id, name);
      else await createPlanStatus(stage.id, name);
      toast.success(isNew ? `${name} added to ${stageName(stage)}` : 'Status renamed');
      onSaved({ name });
    } catch (err) {
      toast.error(getErrorMessage(err, 'The status could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Status' : 'Edit Status'}
      description={isNew ? `A new status under ${stageName(stage)}, added last.` : `A status under ${stageName(stage)}.`}
      isNew={isNew}
      dirty={isNew ? !missing : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <DialogField
        label="Name"
        htmlFor="plan-status-name"
        required
        state={errors.length > 0 ? 'error' : 'none'}
        labelHint={missing ? '— required' : undefined}
        badge={t.badge('name', 'Name')}
      >
        <Input
          id="plan-status-name"
          value={t.form.name}
          placeholder="e.g. Waiting on labs"
          maxLength={PLAN_STATUS_NAME_MAX}
          autoFocus
          onChange={(e) => t.patch({ name: e.target.value })}
          className={fieldClasses.input(errors.length > 0 ? 'error' : 'none')}
        />
      </DialogField>
    </AdminFormModal>
  );
}

export default PlanStatusesAdmin;
